/**
 * PATH: packages/job-hub/src/jobs/qa/qaChecklistRailMenu.ts
 * FILE_ID: JOBHUB-QA-CHECKLIST-RAIL-MENU-001
 * PURPOSE: Right-click menu for one checklist in the build rail.
 */
import { ArrowDown, ArrowUp, ClipboardPaste, Copy, Pencil, Plus, Trash2 } from 'lucide-react'
import type { ContextMenuItem } from '@quotech/ui'

export function buildQaChecklistRailMenu({
  pasteChecklistLabel,
  pasteRowsLabel,
  onCopy,
  onPasteAbove,
  onPasteBelow,
  onPasteRows,
  onRename,
  onDuplicate,
  onInsert,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: {
  pasteChecklistLabel: string | null
  pasteRowsLabel: string | null
  onCopy: () => void
  onPasteAbove: () => void
  onPasteBelow: () => void
  onPasteRows: () => void
  onRename: () => void
  onDuplicate?: () => void
  onInsert?: () => void
  onDelete?: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
  canMoveUp?: boolean
  canMoveDown?: boolean
}): ContextMenuItem[] {
  // Reused from: packages/job-hub/src/jobs/qa/qaChecklistRowMenu.ts (ContextMenuItem builder)
  return [
    { id: 'copy-checklist', label: 'Copy checklist', icon: Copy, onSelect: onCopy },
    {
      id: 'paste-checklist-above',
      label: 'Paste checklist above',
      icon: ClipboardPaste,
      hint: pasteChecklistLabel ?? 'Copy a checklist first',
      disabled: !pasteChecklistLabel,
      onSelect: onPasteAbove,
    },
    {
      id: 'paste-checklist-below',
      label: 'Paste checklist below',
      icon: ClipboardPaste,
      hint: pasteChecklistLabel ?? 'Copy a checklist first',
      disabled: !pasteChecklistLabel,
      onSelect: onPasteBelow,
    },
    {
      id: 'paste-rows',
      label: 'Paste rows at end',
      icon: ClipboardPaste,
      hint: pasteRowsLabel ?? 'Copy a row first',
      disabled: !pasteRowsLabel,
      onSelect: onPasteRows,
    },
    { id: 'sep-rename', kind: 'separator' },
    { id: 'rename', label: 'Rename', icon: Pencil, onSelect: onRename },
    ...(onMoveUp ? [{ id: 'move-up', label: 'Move up', icon: ArrowUp, disabled: canMoveUp === false, onSelect: onMoveUp }] : []),
    ...(onMoveDown ? [{ id: 'move-down', label: 'Move down', icon: ArrowDown, disabled: canMoveDown === false, onSelect: onMoveDown }] : []),
    ...(onDuplicate ? [{ id: 'duplicate-checklist', label: 'Duplicate', icon: Copy, onSelect: onDuplicate }] : []),
    ...(onInsert ? [{ id: 'insert-checklist', label: 'Insert checklist', icon: Plus, onSelect: onInsert }] : []),
    ...(onDelete ? [{ id: 'delete-checklist', kind: 'destructive' as const, label: 'Delete', icon: Trash2, onSelect: onDelete }] : []),
  ]
}
